import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { useToast } from '../ui/toast.jsx';
import { formatUtcInTimezone } from '../../lib/time.js';
import { api, ApiError } from '../../api/index.js';
import {
  LastRefresh, ExportButtons, SearchInput, applySearch,
  useTableState, SortIcon, Pagination,
} from '../../lib/table-utils.jsx';

/**
 * CoT Bridge — admin page. Faithful port of reference
 * `manuals/COP-microservice-integration/CoT Bridge/cot-bridge-page.jsx`.
 *
 * Adjustments vs reference (coord plumbing only):
 *   - PageShell outer chrome (icon + title) — common to every coord page.
 *   - useToast() instead of onToast prop.
 *   - formatUtcInTimezone(iso, tz) instead of formatInTz.
 *   - api.cop.cot.* instead of api.getNatoCot* / api.reconnectNatoCotConnector.
 *   - copLayers fetched locally via api.cop.getLayers().
 *   - 503 -> notInstalled banner (ad-cop-service unreachable diagnostic).
 *
 * The widget structure (sections) is identical to the reference.
 */

const POLL_MS = 5000;
const MSG_PAGE_SIZE = 25;

function statusBadgeClass(status) {
  if (status === 'up')    return 'bg-emerald-50 border border-emerald-200 text-emerald-700';
  if (status === 'down')  return 'bg-slate-50 border border-slate-200 text-slate-600';
  if (status === 'error') return 'bg-rose-50 border border-rose-200 text-rose-700';
  if (status === 'config_load_failed') return 'bg-rose-50 border border-rose-200 text-rose-700';
  return 'bg-slate-50 border border-slate-200 text-slate-500';
}

function statusIcon(status) {
  if (status === 'up')    return 'fa-circle-check';
  if (status === 'down')  return 'fa-circle-stop';
  if (status === 'error') return 'fa-circle-exclamation';
  if (status === 'config_load_failed') return 'fa-triangle-exclamation';
  return 'fa-circle-dot';
}

function transportBadge(t) {
  const map = {
    'multicast':  'bg-sky-50 text-sky-700',
    'tcp':        'bg-violet-50 text-violet-700',
    'tcp-listen': 'bg-violet-50 text-violet-700',
    'tak-mtls':   'bg-amber-50 text-amber-700',
  };
  return map[t] || 'bg-slate-100 text-slate-600';
}

function bytesHuman(n) {
  if (!Number.isFinite(n) || n === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function CopCotBridgePage({ currentUser }) {
  const toast = useToast();
  const tz = currentUser?.timezone || 'UTC';
  const formatDate = useCallback((iso, opts) => iso ? formatUtcInTimezone(iso, tz, opts) : '—', [tz]);

  const [status, setStatus] = useState(null);
  const [copLayers, setCopLayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [messageDir, setMessageDir] = useState('rx');
  const [messages, setMessages] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [savingConfig, setSavingConfig] = useState(false);
  const [draftStale, setDraftStale] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [msgSearch, setMsgSearch] = useState('');
  const [reconnectingPeer, setReconnectingPeer] = useState(null);
  const [notInstalled, setNotInstalled] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const data = await api.cop.cot.status();
      setStatus(data);
      setRefreshedAt(new Date());
      setNotInstalled(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setNotInstalled(err.payload || { reason: 'unknown' });
      } else {
        toast.error(`CoT status failed: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadCopLayers = useCallback(async () => {
    try {
      const res = await api.cop.getLayers();
      setCopLayers(res?.layers || []);
    } catch { /* ignore */ }
  }, []);

  async function handleReconnect(peerId) {
    if (!window.confirm(`Reconnect connector "${peerId}"?\n\nWill stop+restart the underlying socket. Brief drop in RX/TX during the gap.`)) return;
    setReconnectingPeer(peerId);
    try {
      const result = await api.cop.cot.reconnectConnector(peerId);
      if (result?.ok) {
        toast.success(`Connector ${peerId} reconnected`);
        setTimeout(loadStatus, 500);
      } else {
        toast.error(`Reconnect failed: ${result?.error || 'unknown'}`);
      }
    } catch (err) {
      toast.error(`Reconnect failed: ${err.message}`);
    } finally {
      setReconnectingPeer(null);
    }
  }

  const searchedMessages = useMemo(
    () => applySearch(messages, msgSearch, [
      'peerId', 'uid', 'cotType', 'typeId', 'callsign', 'lookupVia',
    ]),
    [messages, msgSearch]
  );
  const msgTable = useTableState(searchedMessages, {
    defaultSortKey: 'ts',
    defaultSortDir: 'desc',
    pageSize: MSG_PAGE_SIZE,
    sortAccessors: {
      ts:       (m) => m.ts || '',
      peerId:   (m) => m.peerId || '',
      cotType:  (m) => m.cotType || '',
      typeId:   (m) => m.typeId || '',
      callsign: (m) => m.callsign || '',
    },
  });

  const messageExportColumns = useMemo(() => [
    { key: 'ts',        label: 'Time (ISO)' },
    { key: 'peerId',    label: 'Peer' },
    { key: 'uid',       label: 'UID' },
    { key: 'cotType',   label: 'CoT type' },
    { key: 'typeId',    label: 'Type ID' },
    { key: 'lat',       label: 'Lat' },
    { key: 'lon',       label: 'Lon' },
    { key: 'callsign',  label: 'Callsign' },
    { key: 'lookupVia', label: 'Via' },
  ], []);

  const connectorExportColumns = useMemo(() => [
    { key: 'id',         label: 'Peer' },
    { key: 'direction',  label: 'Direction' },
    { key: 'transport',  label: 'Transport' },
    { key: 'status',     label: 'Status' },
    { key: 'rxCount',    label: 'RX' },
    { key: 'txCount',    label: 'TX' },
    { key: 'bytesIn',    label: 'Bytes In' },
    { key: 'bytesOut',   label: 'Bytes Out' },
    { key: 'lastUp',     label: 'Last up (ISO)' },
    { key: 'lastDown',   label: 'Last down (ISO)' },
    { key: 'lastError',  label: 'Last error' },
  ], []);

  const loadMessages = useCallback(async (dir) => {
    try {
      const data = await api.cop.cot.messages(dir);
      setMessages(data.messages || []);
    } catch { /* ignore */ }
  }, []);

  const loadUnmapped = useCallback(async () => {
    try {
      const data = await api.cop.cot.unmapped();
      setUnmapped(data.items || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadStatus(); loadUnmapped(); loadCopLayers(); }, [loadStatus, loadUnmapped, loadCopLayers]);
  useEffect(() => { loadMessages(messageDir); }, [messageDir, loadMessages]);

  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      loadStatus();
      loadMessages(messageDir);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [loadStatus, loadMessages, messageDir]);

  async function handleRefresh() {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await Promise.all([loadStatus(), loadMessages(messageDir), loadUnmapped(), loadCopLayers()]);
    } finally {
      setRefreshing(false);
    }
  }

  async function saveConfigPatch(patch, label) {
    setSavingConfig(true);
    try {
      await api.cop.cot.setRuntimeConfig(patch);
      toast.success(`${label} updated`);
      await loadStatus();
    } catch (err) {
      toast.error(`Update failed: ${err.message}`);
    } finally {
      setSavingConfig(false);
    }
  }

  const moduleStatus = status?.moduleStatus || (status?.enabled === false ? 'down' : 'down');
  const isConfigError = moduleStatus === 'config_load_failed';
  const hot = status?.hotConfig || null;
  const exportLayers = hot?.export?.layers || [];
  const staleSeconds = draftStale !== null ? draftStale : (hot?.export?.staleSeconds ?? 300);
  const includeUnknown = hot?.export?.includeUnknownAffiliation ?? false;

  return (
    <PageShell icon="fa-satellite-dish" title="CoT Bridge">
      {notInstalled ? (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <i className="fa-sharp fa-solid fa-triangle-exclamation mr-2" />
          COP ADMS service is not reachable
          {notInstalled.reason ? ` (${notInstalled.reason})` : ''}. Open
          <span className="font-medium"> COP ADMS → Datasets</span> for diagnostics.
        </div>
      ) : loading ? (
        <div className="rounded-[28px] border border-line bg-white p-10 text-center text-sm text-mist">
          Loading CoT Bridge status...
        </div>
      ) : (
        <div className="space-y-5">
          {/* ── Header ── */}
          <section className="rounded-[28px] border border-line bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-slate-700">
                  <i className="fa-sharp fa-solid fa-satellite-dish" />
                  NATO CoT Bridge
                </div>
                <p className="text-sm text-mist">
                  Cursor on Target inbound/outbound — multicast, TCP, TAK-mTLS. Standard: APP-6(D) / MIL-STD-2525D.
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <LastRefresh ts={refreshedAt} />
                <button className="btn-secondary" onClick={handleRefresh} disabled={refreshing}>
                  <i className={`fa-sharp fa-solid fa-arrows-rotate ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
            </div>

            {/* Status row */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass(moduleStatus)}`}>
                <i className={`fa-sharp fa-solid ${statusIcon(moduleStatus)}`} />
                {moduleStatus.replace(/_/g, ' ')}
              </span>
              <span className="text-xs text-mist">
                Mapped types: <span className="font-mono">{status?.mappedTypeCount ?? '—'}</span>
              </span>
              <span className="text-xs text-mist">
                Connectors: <span className="font-mono">{status?.connectors?.length ?? 0}</span>
              </span>
              <span className="text-xs text-mist">
                RX total: <span className="font-mono tabular-nums">{status?.counts?.rxTotal ?? 0}</span>
              </span>
              <span className="text-xs text-mist">
                TX total: <span className="font-mono tabular-nums">{status?.counts?.txTotal ?? 0}</span>
              </span>
              <span className="text-xs text-mist">
                Self-echoes dropped: <span className="font-mono tabular-nums">{status?.counts?.selfEchoesDropped ?? 0}</span>
              </span>
              {status?.startedAt && (
                <span className="text-xs text-mist">
                  Started: <span className="font-mono">{formatDate(status.startedAt)}</span>
                </span>
              )}
            </div>

            {/* Config error block */}
            {isConfigError && (
              <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
                <div className="font-semibold">Configuration error in data/cot.json</div>
                <div className="mt-1 font-mono text-xs">{status?.configError || '(no detail)'}</div>
              </div>
            )}
          </section>

          {/* ── Connectors ── */}
          <section className="rounded-[28px] border border-line bg-white p-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-mist">
                <i className="fa-sharp fa-solid fa-plug text-slate-400" />
                Connectors
              </h3>
              <ExportButtons
                getRows={() => [...(status?.connectors || [])].sort((a, b) => a.id.localeCompare(b.id))}
                columns={connectorExportColumns}
                filenameBase="cot-connectors"
                disabled={(status?.connectors || []).length === 0}
                small
              />
            </div>
            {(status?.connectors || []).length === 0 ? (
              <div className="py-6 text-center text-sm text-mist">
                No connectors configured. Add inbound/outbound peers to <code className="rounded bg-slate-100 px-1 font-mono text-xs">data/cot.json</code> and restart.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-line text-xs font-semibold uppercase tracking-[0.12em] text-mist">
                      <th className="pb-3 pr-4">Peer</th>
                      <th className="pb-3 pr-4">Direction</th>
                      <th className="pb-3 pr-4">Transport</th>
                      <th className="pb-3 pr-4">Status</th>
                      <th className="pb-3 pr-4 text-right">RX</th>
                      <th className="pb-3 pr-4 text-right">TX</th>
                      <th className="pb-3 pr-4 text-right">Bytes In</th>
                      <th className="pb-3 pr-4 text-right">Bytes Out</th>
                      <th className="pb-3 pr-4">Last activity</th>
                      <th className="pb-3 pr-4">Last error</th>
                      <th className="pb-3 pr-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...status.connectors].sort((a, b) => a.id.localeCompare(b.id)).map((c) => {
                      const lastTs = c.lastUp || c.lastDown;
                      const isReconnecting = reconnectingPeer === c.id;
                      return (
                        <tr key={c.id} className="border-b border-line last:border-0 hover:bg-slate-50">
                          <td className="py-3 pr-4 font-mono text-xs text-ink">{c.id}</td>
                          <td className="py-3 pr-4">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                              c.direction === 'inbound' ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'
                            }`}>
                              {c.direction}
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${transportBadge(c.transport)}`}>
                              {c.transport}
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusBadgeClass(c.status)}`}>
                              <i className={`fa-sharp fa-solid ${statusIcon(c.status)} text-[8px]`} />
                              {c.status}
                            </span>
                          </td>
                          <td className="py-3 pr-4 text-right tabular-nums font-mono text-xs text-mist">{c.rxCount}</td>
                          <td className="py-3 pr-4 text-right tabular-nums font-mono text-xs text-mist">{c.txCount}</td>
                          <td className="py-3 pr-4 text-right tabular-nums font-mono text-xs text-mist">{bytesHuman(c.bytesIn)}</td>
                          <td className="py-3 pr-4 text-right tabular-nums font-mono text-xs text-mist">{bytesHuman(c.bytesOut)}</td>
                          <td className="py-3 pr-4 text-xs text-mist tabular-nums">{lastTs ? formatDate(lastTs, { second: '2-digit' }) : '—'}</td>
                          <td className="py-3 pr-4 text-xs text-rose-600 font-mono max-w-[200px] truncate" title={c.lastError || ''}>{c.lastError || '—'}</td>
                          <td className="py-3 pr-4 text-center">
                            <button
                              type="button"
                              className="rounded-lg border border-line bg-white px-2 py-1 text-[11px] font-medium text-mist hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                              onClick={() => handleReconnect(c.id)}
                              disabled={isReconnecting}
                              title="Force stop+restart of this connector (brief drop in RX/TX during the gap)"
                            >
                              <i className={`fa-sharp fa-solid fa-arrows-rotate mr-1 ${isReconnecting ? 'animate-spin' : ''}`} />
                              {isReconnecting ? '...' : 'Reconnect'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ── Imported CoT Layers ── */}
          {(() => {
            const cotLayers = (copLayers || [])
              .filter((l) => (l.datasetId || '').startsWith('cop-ds-cot-'))
              .sort((a, b) => a.name.localeCompare(b.name));
            const totalObjects = cotLayers.reduce((s, l) => s + (l.objectCount || 0), 0);
            return (
              <section className="rounded-[28px] border border-line bg-white p-5">
                <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
                  <h3 className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-mist">
                    <i className="fa-sharp fa-solid fa-satellite-dish text-sky-400" />
                    Imported CoT Layers
                  </h3>
                  <div className="text-xs text-mist">
                    <span className="font-mono tabular-nums">{cotLayers.length}</span> layer{cotLayers.length === 1 ? '' : 's'}
                    {' · '}
                    <span className="font-mono tabular-nums">{totalObjects}</span> object{totalObjects === 1 ? '' : 's'}
                  </div>
                </div>
                <p className="mb-3 text-xs text-mist">
                  Layers received from configured CoT connectors and stored under <code className="rounded bg-slate-100 px-1 font-mono text-[10px]">cop-ds-cot-*</code> datasets. Each represents the current set of tracks reported by a connected peer; objects refresh continuously while the peer is up.
                </p>
                <div className="rounded-xl border border-line bg-slate-50/40">
                  <div className="flex items-center justify-between gap-2 rounded-t-xl border-b border-line bg-sky-50 px-3 py-2 text-sky-700">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                      <i className="fa-sharp fa-solid fa-satellite-dish" />
                      CoT Datasets
                    </div>
                    <span className="rounded-full bg-white/70 px-2 py-0.5 font-mono text-[10px] tabular-nums">
                      {cotLayers.length}
                    </span>
                  </div>
                  <div className="max-h-[1200px] overflow-y-auto p-2 space-y-0.5">
                    {cotLayers.length === 0 ? (
                      <div className="px-2 py-4 text-center text-[11px] text-mist">
                        No CoT layers yet. They appear once a configured inbound connector receives track data.
                      </div>
                    ) : cotLayers.map((l) => (
                      <div key={l.id} className="flex items-center gap-2 rounded p-1 text-xs">
                        <i className="fa-sharp fa-solid fa-layer-group text-[10px] text-mist" />
                        <span className="min-w-0 flex-1 truncate font-mono text-ink" title={l.name}>{l.name}</span>
                        <span className="shrink-0 text-mist tabular-nums">({l.objectCount || 0})</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            );
          })()}

          {/* ── Hot config ── */}
          <section className="rounded-[28px] border border-line bg-white p-5 space-y-5">
            <div>
              <h3 className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-mist">
                <i className="fa-sharp fa-solid fa-bolt text-slate-400" />
                Live Config (no restart needed)
              </h3>
              <p className="mt-1 text-xs text-mist">Changes apply immediately to the running CoT bridge. Audit log records each change.</p>
            </div>

            {/* Export layers — two columns */}
            {(() => {
              const groups = { cop: [], cot: [] };
              for (const layer of copLayers) {
                const dsId = layer.datasetId || '';
                if (dsId.startsWith('cop-ds-cot-')) groups.cot.push(layer);
                else if (dsId.startsWith('cop-ds-nffi-')) continue;
                else groups.cop.push(layer);
              }
              for (const k of Object.keys(groups)) {
                groups[k].sort((a, b) => a.name.localeCompare(b.name));
              }

              const renderColumn = (title, icon, badgeClass, layersList, emptyText) => (
                <div className="flex min-w-0 flex-col rounded-xl border border-line bg-slate-50/40">
                  <div className={`flex items-center justify-between gap-2 rounded-t-xl border-b border-line px-3 py-2 ${badgeClass}`}>
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                      <i className={`fa-sharp fa-solid ${icon}`} />
                      {title}
                    </div>
                    <span className="rounded-full bg-white/70 px-2 py-0.5 font-mono text-[10px] tabular-nums">
                      {layersList.length}
                    </span>
                  </div>
                  <div className="max-h-[1200px] overflow-y-auto p-2 space-y-0.5">
                    {layersList.length === 0 ? (
                      <div className="px-2 py-4 text-center text-[11px] text-mist">{emptyText}</div>
                    ) : layersList.map((l) => (
                      <label key={l.id} className="flex items-center gap-2 rounded p-1 text-xs cursor-pointer hover:bg-white">
                        <input
                          type="checkbox"
                          checked={exportLayers.includes(l.id)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...exportLayers, l.id]
                              : exportLayers.filter((x) => x !== l.id);
                            saveConfigPatch({ exportLayers: next }, 'Export layers');
                          }}
                          disabled={savingConfig}
                        />
                        <span className="min-w-0 flex-1 truncate font-mono" title={l.name}>{l.name}</span>
                        <span className="shrink-0 text-mist tabular-nums">({l.objectCount || 0})</span>
                      </label>
                    ))}
                  </div>
                </div>
              );

              return (
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 text-sm font-medium text-ink">
                    <i className="fa-sharp fa-solid fa-tower-broadcast text-slate-400" />
                    Outbound Streaming Layers
                    <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                      <i className="fa-sharp fa-solid fa-floppy-disk text-[8px]" />
                      auto-saves
                    </span>
                  </div>
                  <div className="text-xs text-mist mb-2">
                    Toggling a checkbox saves immediately. Selected layers stream continuously to outbound peers every {hot?.export?.intervalMs ?? 5000} ms when bridge is enabled. CoT-imported can be re-broadcast to OTHER CoT peers (echo-prevention at runtime via _natoSource tagging). NFFI-imported is excluded — cross-protocol bridging is out of scope of MIL-STD-2525D CoT.
                  </div>
                  {copLayers.length === 0 ? (
                    <div className="rounded-xl border border-line bg-slate-50 p-3 text-xs text-mist">No COP layers available. Create some in the Datasets page.</div>
                  ) : (
                    <div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
                      {renderColumn('COP Datasets',  'fa-folder-open',     'bg-slate-100 text-slate-700',   groups.cop,  'No internal COP layers')}
                      {renderColumn('CoT Datasets',  'fa-satellite-dish',  'bg-sky-50 text-sky-700',         groups.cot,  'No CoT-imported layers (received via inbound)')}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Stale seconds + include unknown */}
            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <label className="inline-flex items-center gap-2 text-sm font-medium text-ink mb-1">
                  <i className="fa-sharp fa-solid fa-hourglass-half text-slate-400" />
                  Stale TTL (seconds)
                </label>
                <div className="text-xs text-mist mb-1.5">CoT &lt;event stale&gt; field — how long receivers should consider our messages valid. Type a value, then click Apply for this field only.</div>
                <div className="flex gap-2">
                  <input
                    type="number"
                    className="field flex-1"
                    min="10"
                    max="86400"
                    value={staleSeconds}
                    onChange={(e) => setDraftStale(Number(e.target.value))}
                    disabled={savingConfig}
                  />
                  <button
                    className="btn-secondary !px-3 !py-1.5 text-xs"
                    onClick={() => { saveConfigPatch({ staleSeconds }, 'Stale TTL'); setDraftStale(null); }}
                    disabled={savingConfig || draftStale === null}
                    title="Apply Stale TTL value"
                  >
                    <i className={`fa-sharp fa-solid ${savingConfig ? 'fa-spinner fa-spin' : 'fa-check'}`} />
                    Apply TTL
                  </button>
                </div>
              </div>

              <div>
                <label className="inline-flex items-center gap-2 text-sm font-medium text-ink mb-1">
                  <i className="fa-sharp fa-solid fa-circle-question text-slate-400" />
                  Include Unknown Affiliation
                  <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                    <i className="fa-sharp fa-solid fa-floppy-disk text-[8px]" />
                    auto-saves
                  </span>
                </label>
                <div className="text-xs text-mist mb-1.5">Export objects with pending/unknown affiliation? Default: off.</div>
                <button
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition ${
                    includeUnknown
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                      : 'border-line bg-slate-50 text-slate-600'
                  }`}
                  onClick={() => saveConfigPatch({ includeUnknownAffiliation: !includeUnknown }, 'Include unknown')}
                  disabled={savingConfig}
                >
                  <i className={`fa-sharp fa-solid ${includeUnknown ? 'fa-toggle-on' : 'fa-toggle-off'}`} />
                  {includeUnknown ? 'Enabled' : 'Disabled'}
                </button>
              </div>
            </div>

            {hot && (
              <div className="inline-flex items-center gap-2 text-xs text-mist border-t border-line pt-3">
                <i className="fa-sharp fa-solid fa-circle-info text-slate-400" />
                Node ID: <span className="font-mono text-ink">{hot.nodeId}</span> ·
                Symbology: <span className="font-mono text-ink">{hot.symbology?.standard}</span> ·
                SIDC length: <span className="font-mono text-ink">{hot.symbology?.sidcLength}</span> ·
                <code className="rounded bg-slate-100 px-1 text-[10px]">__milsymbol</code> detail: <span className="font-mono text-ink">{hot.symbology?.milSymbolDetail ? 'on' : 'off'}</span>
              </div>
            )}
          </section>

          {/* ── Recent messages ── */}
          <section className="rounded-[28px] border border-line bg-white p-5 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-mist">
                <i className="fa-sharp fa-solid fa-clock-rotate-left text-slate-400" />
                Recent Messages
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex gap-1 rounded-xl border border-line p-1">
                  <button
                    className={`px-3 py-1 text-xs font-medium rounded-lg ${messageDir === 'rx' ? 'bg-ink text-white' : 'text-slate-600'}`}
                    onClick={() => setMessageDir('rx')}
                  >
                    <i className="fa-sharp fa-solid fa-download" /> Inbound (RX)
                  </button>
                  <button
                    className={`px-3 py-1 text-xs font-medium rounded-lg ${messageDir === 'tx' ? 'bg-ink text-white' : 'text-slate-600'}`}
                    onClick={() => setMessageDir('tx')}
                  >
                    <i className="fa-sharp fa-solid fa-upload" /> Outbound (TX)
                  </button>
                </div>
                <ExportButtons
                  getRows={() => searchedMessages}
                  columns={messageExportColumns}
                  filenameBase={`cot-messages-${messageDir}`}
                  disabled={searchedMessages.length === 0}
                  small
                />
              </div>
            </div>

            <div className="max-w-md">
              <SearchInput
                value={msgSearch}
                onChange={setMsgSearch}
                placeholder="Search: peer / uid / cot type / callsign / via…"
                small
              />
            </div>

            {messages.length === 0 ? (
              <div className="py-6 text-center text-xs text-mist">No {messageDir.toUpperCase()} messages yet.</div>
            ) : msgTable.pageRows.length === 0 ? (
              <div className="py-6 text-center text-xs text-mist">No messages match the search.</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-line text-[10px] font-semibold uppercase tracking-[0.12em] text-mist">
                        <th className="pb-2 pr-3 cursor-pointer select-none whitespace-nowrap" onClick={() => msgTable.toggleSort('ts')}>
                          Time<SortIcon active={msgTable.sortKey === 'ts'} dir={msgTable.sortDir} />
                        </th>
                        <th className="pb-2 pr-3 cursor-pointer select-none whitespace-nowrap" onClick={() => msgTable.toggleSort('peerId')}>
                          Peer<SortIcon active={msgTable.sortKey === 'peerId'} dir={msgTable.sortDir} />
                        </th>
                        <th className="pb-2 pr-3 whitespace-nowrap">UID</th>
                        <th className="pb-2 pr-3 cursor-pointer select-none whitespace-nowrap" onClick={() => msgTable.toggleSort('cotType')}>
                          CoT type<SortIcon active={msgTable.sortKey === 'cotType'} dir={msgTable.sortDir} />
                        </th>
                        <th className="pb-2 pr-3 cursor-pointer select-none whitespace-nowrap" onClick={() => msgTable.toggleSort('typeId')}>
                          Type ID<SortIcon active={msgTable.sortKey === 'typeId'} dir={msgTable.sortDir} />
                        </th>
                        {messageDir === 'rx' && <th className="pb-2 pr-3 whitespace-nowrap">Lat,Lon</th>}
                        {messageDir === 'rx' && <th className="pb-2 pr-3 cursor-pointer select-none whitespace-nowrap" onClick={() => msgTable.toggleSort('callsign')}>
                          Callsign<SortIcon active={msgTable.sortKey === 'callsign'} dir={msgTable.sortDir} />
                        </th>}
                        {messageDir === 'rx' && <th className="pb-2 pr-3 whitespace-nowrap">Via</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {msgTable.pageRows.map((m, idx) => (
                        <tr key={idx} className="border-b border-line last:border-0 hover:bg-slate-50">
                          <td className="py-2 pr-3 font-mono text-mist tabular-nums">{m.ts ? formatDate(m.ts, { second: '2-digit', fractionalSecondDigits: 0 }) : '—'}</td>
                          <td className="py-2 pr-3 font-mono text-ink">{m.peerId}</td>
                          <td className="py-2 pr-3 font-mono text-mist max-w-[180px] truncate" title={m.uid}>{m.uid || '—'}</td>
                          <td className="py-2 pr-3 font-mono text-mist">{m.cotType || '—'}</td>
                          <td className="py-2 pr-3 font-mono text-mist">{m.typeId || '—'}</td>
                          {messageDir === 'rx' && <td className="py-2 pr-3 font-mono text-mist tabular-nums">{m.lat?.toFixed(4)},{m.lon?.toFixed(4)}</td>}
                          {messageDir === 'rx' && <td className="py-2 pr-3 text-ink">{m.callsign || '—'}</td>}
                          {messageDir === 'rx' && (
                            <td className="py-2 pr-3">
                              <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${
                                m.lookupVia === 'milsymbol' ? 'bg-emerald-50 text-emerald-700' :
                                m.lookupVia === 'cot_exact' ? 'bg-sky-50 text-sky-700' :
                                m.lookupVia === 'cot_prefix' ? 'bg-amber-50 text-amber-700' :
                                'bg-rose-50 text-rose-700'
                              }`}>
                                {m.lookupVia || 'unmapped'}
                              </span>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={msgTable.page}
                  totalPages={msgTable.totalPages}
                  total={msgTable.total}
                  pageSize={MSG_PAGE_SIZE}
                  setPage={msgTable.setPage}
                />
              </>
            )}
          </section>

          {/* ── Unmapped types ── */}
          {unmapped.length > 0 && (
            <section className="rounded-[28px] border border-line bg-white p-5 space-y-2">
              <h3 className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-mist">
                <i className="fa-sharp fa-solid fa-circle-exclamation text-amber-500" />
                Unmapped CoT Types
              </h3>
              <p className="text-xs text-mist">Inbound CoT types with no SIDC mapping (fell back to &quot;Unspecified&quot; in the COP). Each entry was logged with audit event <code className="rounded bg-slate-100 px-1 font-mono text-[10px]">cot_unmapped_type</code>.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-line text-[10px] font-semibold uppercase tracking-[0.12em] text-mist">
                      <th className="pb-2 pr-3">CoT type</th>
                      <th className="pb-2 pr-3 text-right">Count</th>
                      <th className="pb-2 pr-3">First seen</th>
                      <th className="pb-2 pr-3">Last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmapped.map((u) => (
                      <tr key={u.cotType} className="border-b border-line last:border-0 hover:bg-slate-50">
                        <td className="py-2 pr-3 font-mono text-ink">{u.cotType}</td>
                        <td className="py-2 pr-3 text-right tabular-nums font-mono text-mist">{u.count}</td>
                        <td className="py-2 pr-3 font-mono text-mist tabular-nums">{formatDate(u.firstSeen)}</td>
                        <td className="py-2 pr-3 font-mono text-mist tabular-nums">{formatDate(u.lastSeen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}
    </PageShell>
  );
}
